import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import React, { useCallback } from 'react';
import { ContainerWrapper, Thumbnail, FeaturedPopularTourWrapper } from './ThingToDoComponent.styles';
import { pxToRem } from '@shared/joykit/packages/core/common';
import { Link } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { truncateString } from './utils';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  hed6ToSubHeadingOverride,
  body1ToSubHeadingOverride,
  hed1ToSubHeadingOverride
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useCustomPageTelemetry } from '../../Custom.telemetry';
import { useClientReference } from '@shared/hooks/useClientReference';

export interface ThingToDoComponentProps {
  id: string;
  sortOrder: number;
  title: string;
  category?: string;
  description: string;
  image?: { id: string; url: string };
  url: string;
  tours?: Array<{ id: string; title: string; description: string; url: string; image: { id: string; url: string } }>;
  designType?: 'fullRow' | 'widget';
  location?: string;
}
export const ThingToDoComponent: React.FC<ThingToDoComponentProps> = ({ designType = 'fullRow', ...props }: ThingToDoComponentProps) => {
  const { t } = useTranslation('guestSiteCustom');
  const telemetry = useCustomPageTelemetry();
  const { clientReference } = useClientReference();
  const constructCampaignUrl = useCallback(
    (url: string) => {
      const campaignUrl = new URL(url);
      campaignUrl.searchParams.set('campaign', clientReference);
      return campaignUrl.href;
    },
    [clientReference]
  );

  return (
    <ContainerWrapper
      flexDirection={designType === 'fullRow' ? ['column', 'column', 'row', 'row'] : 'column'}
      as="section"
      alignItems={designType === 'fullRow' ? ['center', 'center', 'stretch', 'stretch'] : 'center'}
    >
      {/* eslint-disable-next-line react/jsx-no-target-blank */}
      <a
        href={constructCampaignUrl(props.url)}
        target="_blank"
        rel="noopener sponsored nofollow"
        referrerPolicy="origin"
        onClick={() => {
          telemetry.defaultThingsToDoCTAClicked(props.url, {
            componentName: 'DefaultThingsToDo',
            url: props.url,
            thingToDoName: props.title,
            city: props.location,
            tours: props.tours
          });
        }}
      >
        <Box maxWidth={['100%', pxToRem(440)]} flex="1" width="100%" cursor="pointer">
          {props.image?.url && (
            <Box as="figure" width="100%" flex="1" borderRadius={4} overflow="hidden" height={pxToRem(216)}>
              <picture>
                <source srcSet={props.image.url} />
                <Thumbnail src={props.image.url} alt={props.title} height={pxToRem(216)} />
              </picture>
            </Box>
          )}
          <Flex flexDirection="column" paddingBottom={[0, pxToRem(24)]} paddingTop={pxToRem(24)} gap={pxToRem(16)}>
            <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
              <TextV2 as="h2" typographyVariant="hed6">
                {props.title}
              </TextV2>
            </GuestSiteTypographyOverride>
            <GuestSiteTypographyOverride override={body1ToSubHeadingOverride}>
              <TextV2 typographyVariant="body1" color="mono12">
                {props.description}
              </TextV2>
            </GuestSiteTypographyOverride>
          </Flex>
        </Box>
      </a>
      <Box maxWidth={['100%', pxToRem(352)]} as="aside" flex="1" width="100%">
        <GuestSiteTypographyOverride override={hed1ToSubHeadingOverride}>
          <TextV2 as="h3" typographyVariant="hed1">
            {t('thingsToDo', 'popularNearby')()}
          </TextV2>
        </GuestSiteTypographyOverride>
        {props.tours?.map((tour, index) => (
          <FeaturedPopularTour
            isLast={props.tours?.length === index + 1}
            key={tour.id}
            id={tour.id}
            title={tour.title}
            description={tour.description}
            url={constructCampaignUrl(tour.url)}
            image={tour.image}
          />
        ))}
        <Box paddingY={pxToRem(16)}>
          <ButtonV2
            as="a"
            href={props.url}
            target="_blank"
            rel="noopener sponsored nofollow"
            referrerPolicy="origin"
            onClick={() => {
              telemetry.defaultThingsToDoCTAClicked(props.url, {
                componentName: 'DefaultThingsToDo',
                url: props.url,
                thingToDoName: props.title,
                city: props.location,
                tours: props.tours
              });
            }}
            endIcon={<Link />}
            fontFamily="Inter UI"
            variant="outline"
            color="mono14"
            fontWeight={600}
            intent="neutral"
            borderColor="mono5"
            shape="rounded"
            fullWidth
          >
            {t('thingsToDo', 'seeMore')()}
          </ButtonV2>
        </Box>
      </Box>
    </ContainerWrapper>
  );
};

interface PopularTourProps {
  id: string;
  title: string;
  description: string;
  url: string;
  image: { id: string; url: string };
  isLast?: boolean;
  location?: string;
}
const FeaturedPopularTour: React.FC<PopularTourProps> = props => {
  const telemetry = useCustomPageTelemetry();
  return (
    <FeaturedPopularTourWrapper
      isLast={props.isLast ?? false}
      as="a"
      href={props.url}
      target="_blank"
      rel="noopener sponsored nofollow"
      referrerPolicy="origin"
      onClick={() => {
        telemetry.defaultThingsToDoTourClicked(props.url, {
          componentName: 'DefaultThingsToDo',
          url: props.url,
          thingToDoName: props.title,
          city: props.location
        });
      }}
    >
      <Box width={pxToRem(64)} height={pxToRem(64)} as="figure" objectFit="cover" borderRadius={2} overflow="hidden">
        <picture>
          <source srcSet={props.image.url} />
          <Thumbnail src={props.image.url} alt={props.title} height={pxToRem(64)} />
        </picture>
      </Box>
      <Box flex="1">
        <TextV2 as="h3" typographyVariant="hed1" fontFamily="Inter UI" fontWeight={600} fontSize="0.9375rem" lineHeight="1.4" letterSpacing="0em">
          {props.title}
        </TextV2>
        <TextV2
          as="p"
          title={props.description}
          typographyVariant="label1"
          color="mono12"
          truncate
          marginTop={pxToRem(4)}
          fontFamily="Inter UI"
          fontSize="12px"
          fontWeight={400}
          lineHeight="140%"
        >
          {truncateString(props.description)}
        </TextV2>
      </Box>
    </FeaturedPopularTourWrapper>
  );
};
